import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Mousewheel, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { isVideoFile } from '../utils/mediaUtils';

function TrippinReviewPost({ isTagged = false }) {
  const { trippinId } = useParams();
  const navigate = useNavigate();

  const [selectedPhotos, setSelectedPhotos] = useState({});
  const [placeDetails, setPlaceDetails] = useState({});
  const [placesMetadata, setPlacesMetadata] = useState([]);
  const [posting, setPosting] = useState(false);
  const [background, setBackground] = useState(null);
  const [signedPhotos, setSignedPhotos] = useState({});
  const [signedBackground, setSignedBackground] = useState(null);
  const swiperRef = useRef(null);

  const generateSignedUrls = async (photos, backgroundUrl) => {
    try {
      const signedPhotosMap = {};
      for (const placeName in photos) {
        const photoUrls = photos[placeName];
        const signedUrls = await Promise.all(
          photoUrls.map(async (filePath) => {
            const res = await axiosInstance.get('/trippinPosts/generateSignedUrl', { params: { filePath } });
            return res.data.signedUrl;
          })
        );
        signedPhotosMap[placeName] = signedUrls;
      }
      setSignedPhotos(signedPhotosMap);

      if (backgroundUrl) {
        const res = await axiosInstance.get('/generateSignedUrl', { params: { filePath: backgroundUrl } });
        setSignedBackground(res.data.signedUrl);
      }
    } catch (err) {
      console.error('Error generating signed URLs:', err);
    }
  };

  useEffect(() => {
    const photos = JSON.parse(sessionStorage.getItem('selectedPhotos')) || {};
    const details = JSON.parse(sessionStorage.getItem('placeDetails')) || {};
    const backgroundUrl = sessionStorage.getItem('background_url') || null;

    setSelectedPhotos(photos);
    setPlaceDetails(details);
    setBackground(backgroundUrl);

    generateSignedUrls(photos, backgroundUrl);
    fetchPlacesMeta();
  }, [trippinId]);

  const fetchPlacesMeta = async () => {
    try {
      const endpoint = isTagged
        ? `/trippins/${trippinId}/placesTagged`
        : `/trippins/${trippinId}/places`;
      const res = await axiosInstance.get(endpoint);
      setPlacesMetadata(res.data);
    } catch (err) {
      console.error('Error fetching places metadata:', err);
    }
  };

  const handlePost = async (isCloseFriends) => {
    setPosting(true);
    const payload = {
      trippin_id: trippinId,
      selectedPhotos,
      placeDetails,
      background_url: background,
      isCloseFriends,
    };
    //console.log("Posting payload:", payload);

    try {
      const res = await axiosInstance.post('/trippinPosts', payload);
      navigate(`/trippin/postView/${res.data.trippin_post_id}`);
    } catch (err) {
      console.error('Error creating post:', err);
    } finally {
      setPosting(false);
    }
  };
    
  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0.5rem 1rem',
          background: '#fff',
          borderBottom: '1px solid #ddd',
        }}
      >
        <img
          src="/defaultProfile.jpg"
          alt="User"
          style={{ width: 40, height: 40, borderRadius: '50%', marginRight: '0.75rem' }}
        />
        <span style={{ fontWeight: 'bold', marginRight: 'auto' }}>YourUsername</span>
        <div style={{ marginLeft: '1rem' }}>3 days • 200 km</div>
        <button
          style={{
            background: 'none',
            border: 'none',
            fontSize: '1rem',
            marginLeft: '0.5rem',
            cursor: 'pointer',
          }}
        >
          ...
        </button>
      </div>

      {/* Vertical Swiper */}
      <Swiper
        direction="vertical"
        modules={[Navigation, Pagination, Mousewheel, Keyboard]}
        keyboard
        pagination={{ clickable: true }}
        style={{ width: '100%', height: 'calc(100% - 56px)' }}
      >
        {placesMetadata.map((place, placeIndex) => {
          const photosForPlace = signedPhotos[place.place_name] || [];
          const details = placeDetails[place.place_name] || {};

          if (!photosForPlace.length) return null;

          return (
            <SwiperSlide key={place.place_id}>
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Horizontal Swiper for Photos */}
                <Swiper
                  onSwiper={(swiper) => (swiperRef.current = swiper)}
                  modules={[Navigation, Pagination, Mousewheel, Keyboard]}
                  navigation
                  pagination={{ clickable: true }}
                  keyboard
                  nested
                  style={{ flex: 1, background: '#000' }}
                >
                  {photosForPlace.map((photoUrl, imgIndex) => (
                    <SwiperSlide key={`${place.place_id}-${imgIndex}`}>
                      {isVideoFile(photoUrl) ? (
    <video
      src={photoUrl}
      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
      autoPlay
      loop
      playsInline
      onLoadedData={() => swiperRef.current?.update()}
    />
  ) : (
    <img
      src={photoUrl}
      alt={`place-${placeIndex}-img-${imgIndex}`}
      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
      onLoad={() => swiperRef.current?.update()}
    />
  )}
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* Place Details */}
                <div
                  style={{
                    flex: '0 1 auto',
                    background: '#fafafa',
                    padding: '1rem',
                    overflowY: 'auto',
                    position: 'relative',
                  }}
                >
                  <h3 style={{ marginTop: 0 }}>{place.place_name}</h3>
                  <p>Country: {place.country}</p>
                  <p>City: {place.city}</p>
                  <p>Caption: {details.caption}</p>
                  <p>
                    Tags:{" "}
                    {details.tags && details.tags.length > 0
                      ? details.tags.map((u) => u.username).join(', ')
                      : 'No tags added'}
                  </p>
                  <p>Companionship: {details.companionship}</p>
                  <p>Review: {details.review} stars</p>
                  <p>Price: {details.price} {details.currency}</p>
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      background: 'rgba(0,0,0,0.4)',
                      color: '#fff',
                      padding: '4px 8px',
                      borderRadius: '4px',
                    }}
                  >
                    {placeIndex + 1}/{placesMetadata.length}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {/* Post Buttons */}
      <button
        onClick={() => handlePost(true)}
        disabled={posting}
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '100px',
          background: '#007bff',
          color: '#fff',
          border: 'none',
          padding: '0.75rem 1.5rem',
          borderRadius: '4px',
          cursor: posting ? 'not-allowed' : 'pointer',
          zIndex: 1000,
        }}
      >
        {posting ? 'Posting...' : 'Post Close'}
      </button>
      <button
        onClick={() => handlePost(false)}
        disabled={posting}
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          background: '#007bff',
          color: '#fff',
          border: 'none',
          padding: '0.75rem 1.5rem',
          borderRadius: '4px',
          cursor: posting ? 'not-allowed' : 'pointer',
          zIndex: 1000,
        }}
      >
        {posting ? 'Posting...' : 'Post'}
      </button>
    </div>
  );
}

export default TrippinReviewPost;
